import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pencil, Power, Globe } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Modal } from "@/components/ui/Modal.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { adminApi, type AdminTenant } from "@/api/admin.ts";

interface TenantForm {
  [key: string]: unknown;
  slug: string;
  domain: string;
  name: string;
  tagline: string;
  primaryColor: string;
  accentColor: string;
}

const emptyForm: TenantForm = {
  slug: "", domain: "", name: "", tagline: "", primaryColor: "#4f46e5", accentColor: "#06b6d4",
};

export function AdminTenantsPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<"create" | "edit" | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<TenantForm>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "tenants"],
    queryFn: () => adminApi.listTenants(),
  });

  const create = useMutation({
    mutationFn: () => adminApi.createTenant(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "tenants"] }); setModal(null); setForm(emptyForm); },
  });

  const update = useMutation({
    mutationFn: () => adminApi.updateTenant(editId!, form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "tenants"] }); setModal(null); setEditId(null); setForm(emptyForm); },
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminApi.updateTenant(id, { isActive: !isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "tenants"] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteTenant(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "tenants"] }); setDeleteId(null); },
  });

  const tenants: AdminTenant[] = data?.tenants ?? [];

  const openEdit = (t: AdminTenant) => {
    setEditId(t.id);
    setForm({
      slug: t.slug,
      domain: t.domain,
      name: t.name,
      tagline: t.tagline ?? "",
      primaryColor: t.primaryColor,
      accentColor: t.accentColor,
    });
    setModal("edit");
  };

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-4xl p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-surface-900">Tenants</h1>
        <Button size="sm" onClick={() => { setForm(emptyForm); setEditId(null); setModal("create"); }}>
          <Plus className="w-4 h-4 mr-1" /> Add Tenant
        </Button>
      </div>

      <div className="space-y-3">
        {tenants.length === 0 && (
          <Card><CardContent className="py-8 text-center text-surface-400 text-sm">No tenants configured.</CardContent></Card>
        )}
        {tenants.map((t) => (
          <Card key={t.id}>
            <CardContent className="py-4 flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm text-surface-900">{t.name}</span>
                  <Badge variant={t.isActive ? "success" : "muted"}>{t.isActive ? "Active" : "Inactive"}</Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-surface-500">
                  <span className="font-mono">{t.slug}</span>
                  <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{t.domain}</span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: t.primaryColor }} />
                    <span className="w-3 h-3 rounded-full border" style={{ backgroundColor: t.accentColor }} />
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => toggleActive.mutate({ id: t.id, isActive: t.isActive })} title={t.isActive ? "Deactivate" : "Activate"}>
                  <Power className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" aria-label="Edit this tenant" onClick={() => openEdit(t)}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" aria-label="Delete this tenant" onClick={() => setDeleteId(t.id)}>
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create / Edit modal */}
      <Modal
        open={modal !== null}
        onClose={() => { setModal(null); setEditId(null); setForm(emptyForm); }}
        title={modal === "create" ? "New Tenant" : "Edit Tenant"}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-surface-600 mb-1">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme Videos" />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-600 mb-1">Slug</label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="acme" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-600 mb-1">Domain</label>
            <Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="acme.tubeforge.io" />
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-600 mb-1">Tagline</label>
            <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="AI-powered video creation" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-surface-600 mb-1">Primary Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.primaryColor} onChange={(e) => setForm({ ...form, primaryColor: e.target.value })} className="w-8 h-8 rounded cursor-pointer" />
                <Input value={form.primaryColor} onChange={(e) => setForm({ ...form, primaryColor: e.target.value })} className="flex-1 font-mono text-xs" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-600 mb-1">Accent Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.accentColor} onChange={(e) => setForm({ ...form, accentColor: e.target.value })} className="w-8 h-8 rounded cursor-pointer" />
                <Input value={form.accentColor} onChange={(e) => setForm({ ...form, accentColor: e.target.value })} className="flex-1 font-mono text-xs" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { setModal(null); setEditId(null); setForm(emptyForm); }}>Cancel</Button>
          <Button
            onClick={() => modal === "create" ? create.mutate() : update.mutate()}
            disabled={create.isPending || update.isPending || !form.name.trim() || !form.slug.trim() || !form.domain.trim()}
          >
            {(create.isPending || update.isPending) ? <Spinner className="w-4 h-4" /> : modal === "create" ? "Create" : "Save"}
          </Button>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal open={deleteId !== null} onClose={() => setDeleteId(null)} title="Delete Tenant" size="sm">
        <p className="text-sm text-surface-600 mb-4">Are you sure? This will remove the tenant and all associated data. This action cannot be undone.</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => deleteId && remove.mutate(deleteId)} disabled={remove.isPending}>
            {remove.isPending ? <Spinner className="w-4 h-4" /> : "Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
